import {
  useAccountId,
  useCurrentCloudProvider,
  useTenantPartnerId,
  ShortenedNameV2,
  updateUserWalkthrough,
  type UserProfileState,
} from '@labrav/common'
import {
  LabraToolTipV2,
  ModalConfirmV2,
  ModalSizeVariants,
  StatusBar,
  StatusBarColors,
  StatusBarDividerColors,
  SidePanelV3,
} from '@labrav/react-components'

import { AddEditProductListingFooter } from '../AddEditProductListingFooter/AddEditProductListingFooter'
import { useStyles } from './AddEditProductListing.styles'
import React, { useEffect, useMemo, useState, useCallback } from 'react'

import { FontAwesomeIcon } from '@fortawesome/react-fontawesome'
import {
  faList,
  faShelves,
  faInputText,
  faBell,
  faOctagonExclamation,
  faCircleCheck,
  faArrowLeft,
} from '@fortawesome/pro-regular-svg-icons'

import {
  FlyoutListingProductType,
  FlyoutProductAddEditSectionName,
  FlyoutProductSections,
} from '../../../types/addEditProduct'
import { Form, Formik } from 'formik'

import FlyoutProductValidationSchema from '../../../common/utils/FlyoutProductValidationSchema'
import { useLocation, useNavigate, useParams } from 'react-router-dom'
import {
  getProductCloneDetail,
  getProductInCreation,
  setErrorOnFinalSubmit,
} from '../../../modules/addEditProduct/action'
import { useDispatch, useSelector } from '../../../store/hooks'
import { CloudType } from '../../../types/index'
import { FlyOutAWSProductListingType } from '../../../modules/productsListing/types'
import { isEmpty } from 'lodash'

import { FlyOutProductDetails } from './FlyOutProductDetailsSection/FlyOutProductDetailsSection'
import { FlyOutListingUserType } from '../../../types/external'
import { FlyOutNotificationSection } from './FlyOutNotificationSection/FlyOutNotificationSection'
import { FlyoutLandingPageSection } from './FlyoutLandingPageSection/FlyoutLandingPageSection'
import { FlyoutProductDimensionsSection } from './FlyoutProductDimensionsSection/FlyoutProductDimensionsSection'
import { footerStateType } from '../../../common/components/FooterV2/FooterV2'
import {
  SFBanerText,
  getDisableFormLockTooltip,
  getIsProductSectionCompleted,
  getPageProductCreation,
  getProductCompletePercentage,
  productDetailDate,
} from '../../../common/utils/productCreationUtil'
import clsx from 'clsx'
import { v4 as uuidv4 } from 'uuid'
import { DateTime } from 'luxon'
import { LockMetadata } from '../../../types/addEditProduct'
import { Divider, Typography } from '@mui/material'
import {
  autoSaveThresoldTime,
  awsRequirementsLinkLabra,
  awsRequirementsText,
} from '../../../common/utils/constants'
import { validateProductCreationFlow } from '../../../common/utils/productCreationUtil'
import { SnackBar, TeamMembersV2 } from '@labrav/common'
import { getProducts } from '../../../modules/productsListing/action'
import { ScrollToFieldError, SelectedCloudIcon } from '@labrav/common'
import { useFlag } from '@labrav/flags'
import SalesforceLoadingScreen from '../SalesforceLoadingScreen/SalesforceLoadingScreen'
import { TitleDraftBadge } from '../../../common/components/TitleDraftBadge/TitleDraftBadge'
import { SecondaryNavigationRoutesType } from '@labrav/react-components/lib/@types/v3/components/SidePanel'
import { RequirementsActionV2 } from '../../../common/components/RequirementsActionV2/RequirementsActionV2'
import { ProductVideoAction } from '../../../common/components/ProductVideoAction/ProductVideoAction'
import Joyride, { CallBackProps, Placement, STATUS, Step } from 'react-joyride'
import { WalkthroughTooltip } from '../../../common/components/WalkthroughTooltip/WalkthroughtTooltip'
import { useAuth0 } from '@auth0/auth0-react'
import { useWaitForElement } from '@labrav/common'
import { useSyncFlyoutRouteParams } from '../../../common/utils/hooks/useSyncFlyoutRouteParams'
import { ExternalPlatformType } from '../../ProductListingV2/types'

const creationRoutes: SecondaryNavigationRoutesType[] = [
  {
    key: '2',
    paths: ['/product-details'],
    name: 'Product details',
    disabled: false,
    icon: () => (
      <FontAwesomeIcon
        icon={faList}
        style={{ width: '20px', height: '16px' }}
      />
    ),
  },
  {
    key: '3',
    paths: ['/product-dimensions'],
    name: 'Product dimensions',
    disabled: false,
    icon: () => (
      <FontAwesomeIcon
        icon={faShelves}
        style={{ width: '20px', height: '16px' }}
      />
    ),
  },
  {
    key: '4',
    paths: ['/landing-page'],
    name: 'Buyer registration page',
    disabled: false,
    icon: () => (
      <FontAwesomeIcon
        icon={faInputText}
        style={{ width: '20px', height: '16px' }}
      />
    ),
  },
  {
    key: '5',
    paths: ['/notification-recipients'],
    name: 'Notifications',
    disabled: false,
    icon: () => (
      <FontAwesomeIcon
        icon={faBell}
        style={{ width: '20px', height: '16px' }}
      />
    ),
  },
]

const CompletedIcon = () => (
  <FontAwesomeIcon
    icon={faCircleCheck}
    data-testid="checkmark"
    style={{ height: '16px', width: '16px' }}
    color="green"
  />
)

interface Walkthrough {
  run: boolean
  steps: Step[]
  stepIndex: number
}

export const AddEditProductListing: React.FC<{
  isImpartner?: boolean
  externalPlatformType?: ExternalPlatformType
}> = ({ isImpartner = false, externalPlatformType }) => {
  // Sync route params to selectedAccountIds for FLYOUT
  useSyncFlyoutRouteParams()

  const { search: queryString } = useLocation()
  const awsProductType = new URLSearchParams(queryString).get('awsProductType')
  const listingType = new URLSearchParams(queryString).get('listingTypeInfo')
  const cloneProductId = new URLSearchParams(queryString).get('cloneProductId')
  const { classes } = useStyles()
  const selectedCloudMarketplace = useCurrentCloudProvider()
  const cloneProductName =
    new URLSearchParams(queryString).get('cloneProductName') || ''

  const hideNotificationPage =
    externalPlatformType === ExternalPlatformType.IMPARTNER

  const hideLandingPage =
    externalPlatformType === ExternalPlatformType.IMPARTNER

  const hideMultipleSections = hideLandingPage && hideNotificationPage

  const isDraftListing = new URLSearchParams(queryString).get('isDraftListing')
  const {
    productId = '',
    cloud,
    accountId: routeAccountId,
  } = useParams<{
    productId?: string
    cloud?: string
    accountId?: string
  }>()
  const dispatch = useDispatch()
  const location = useLocation()
  const navigate = useNavigate()
  const partnerId = useTenantPartnerId()
  const defaultAccountId = useAccountId({ product: 'flyout' })
  const currentAccountId = routeAccountId || defaultAccountId || ''

  const { userProfile } = useSelector<UserProfileState>(
    state => state.userProfile
  )

  const { user } = useAuth0()

  const steps: Step[] = [
    {
      content:
        'Get it right the first time—review AWS guidelines to ensure quick approval!',
      placement: 'right' as Placement,
      target: '#aws-requirements',
      spotlightPadding: 0,
      disableBeacon: true,
      styles: {
        tooltip: {
          backgroundColor: '#2E3132',
          color: '#DBE4E6',
          borderRadius: '8px',
        },
      },
    },
    {
      content: 'Save time & avoid errors—watch this demo before you start!',
      spotlightPadding: 0,
      disableBeacon: true,
      placement: 'right' as Placement,
      target: '#video-section',
      styles: {
        tooltip: {
          backgroundColor: '#2E3132',
          color: '#DBE4E6',
          borderRadius: '8px',
        },
      },
    },
    {
      content: 'Speed up listing creation—invite your team to collaborate!',
      placement: 'right' as Placement,
      disableBeacon: true,
      target: '#team-members',
      styles: {
        tooltip: {
          backgroundColor: '#2E3132',
          color: '#DBE4E6',
          borderRadius: '8px',
        },
      },
    },
  ]

  const [walkThrough, setWalkThrough] = useState<Walkthrough>({
    run: false,
    steps: [],
    stepIndex: 0,
  })

  const currentStep =
    steps?.length > walkThrough?.stepIndex
      ? steps[walkThrough?.stepIndex]
      : null
  const isTargetReady = useWaitForElement((currentStep?.target || '') as string)

  const handleJoyrideCallback = (joyRideResponse: CallBackProps) => {
    const { type, status, action } = joyRideResponse

    if (type === 'step:after' && action === 'next') {
      setWalkThrough(prev => ({
        ...prev,
        stepIndex: walkThrough?.stepIndex + 1,
      }))
    }

    if (type === 'step:before' && action === 'prev') {
      setWalkThrough(prev => ({
        ...prev,
        stepIndex: walkThrough?.stepIndex - 1,
      }))
    }

    if (([STATUS.FINISHED, STATUS.SKIPPED] as string[]).includes(status)) {
      setWalkThrough(prev => ({ ...prev, run: false }))
      if (partnerId && userProfile?.id) {
        dispatch(
          updateUserWalkthrough({
            partnerId,
            data: {
              flyout: {
                productListingWalkthrough: true,
              },
              oppsync: {},
            },
            userId: userProfile?.id,
            userPartnerId: user ? user.partner_user_id : '',
          })
        )
      }
    }
  }

  const flyoutProductForm = useSelector<FlyoutProductSections>(
    state => state.flyoutProductInCreation.currentProductInCreation.productForm
  )
  const isErrorOnFinalSubmit = useSelector<string>(
    state =>
      state.flyoutProductInCreation.currentProductInCreation.errorOnSubmit
  )
  const isFailedError = useSelector<string>(
    state => state.flyoutProductInCreation.currentProductInCreation?.error || ''
  )
  const updatedAt = useSelector<string>(
    state =>
      state.flyoutProductInCreation.currentProductInCreation?.updatedAt || ''
  )
  const { fullDateTime: updatedAtValue } = updatedAt
    ? productDetailDate(updatedAt)
    : { fullDateTime: '' }

  const companyName = useSelector<string>(
    state => state.AccountData.user?.partnerData?.companyName ?? ''
  )

  const { flag: isFreeTrialProductEnable, loading: flagLoadingFreeTrial } =
    useFlag('freeTrialInProduct')

  const [activeLink, setActiveLink] = React.useState('/product-details')
  const [page, setPage] = useState<FlyoutProductAddEditSectionName>(
    FlyoutProductAddEditSectionName.GENERAL_INFORMATION
  )
  const [isSFFirstTime, setIsSFFirstTime] = useState(false)

  const [lockStatus, setLockStatus] = useState('')

  const [startEditTime, setStartEditTime] = useState<string | undefined>()

  const [isOpenLockModal, setIsOpenLockModal] = useState<boolean>(false)
  const handleCloseLock = useCallback(() => {
    setIsOpenLockModal(false)
  }, [])
  const showModalLock = useCallback(() => {
    setIsOpenLockModal(true)
  }, [])

  const isFormLocked = lockStatus !== '' && selectedCloudMarketplace === 'AWS'

  const isMigrated =
    flyoutProductForm?.[FlyoutProductAddEditSectionName.CLOUD_PRODUCT_DETAILS]
      ?.listingType === FlyoutListingProductType.MIGRATED
  const navigateRoute = (path: string) => {
    setActiveLink(path)
  }
  const FlyoutOnboardingRoutes: {
    title: string
    routes: SecondaryNavigationRoutesType[]
  } = {
    title: 'Listing creation steps -',
    routes: creationRoutes
      .filter(routeItem => (isMigrated ? routeItem.key !== '3' : true))
      .map(creationRoute => {
        const isProductSectionCompleted = getIsProductSectionCompleted(
          creationRoute?.paths ? creationRoute?.paths[0] : '',
          flyoutProductForm[FlyoutProductAddEditSectionName.PAGE_MARKER]
        )
        return {
          ...creationRoute,
          onClick: () =>
            setActiveLink(creationRoute?.paths ? creationRoute?.paths[0] : ''),
          ...(isProductSectionCompleted ? { endIcon: CompletedIcon } : {}),
          ...(creationRoute?.key === '2' && isMigrated
            ? { name: 'Product Listing' }
            : {}),
        }
      }),
  }
  useEffect(() => {
    const isFirstTime = sessionStorage.getItem('isSFFirstTime') === 'true'
    if (isFirstTime) {
      setIsSFFirstTime(true)
      sessionStorage.removeItem('isSFFirstTime')
    }
  }, [])

  // Redirect old routes to new pattern
  useEffect(() => {
    if (!cloud && !routeAccountId && defaultAccountId) {
      const cloudPath = selectedCloudMarketplace.toLowerCase()
      const currentPath = location.pathname
      const isOldRoute = currentPath.includes('/flyout/products-listing')

      if (isOldRoute) {
        const newPath = currentPath.replace(
          '/flyout/products-listing',
          `/flyout/${cloudPath}/${defaultAccountId}/products-listing`
        )
        navigate(
          {
            pathname: newPath,
            search: location.search,
          },
          { replace: true }
        )
      }
    }
  }, [
    cloud,
    routeAccountId,
    defaultAccountId,
    selectedCloudMarketplace,
    location.pathname,
    location.search,
    navigate,
  ])

  useEffect(() => {
    const contentEle = document.getElementById('product-creation-top')
    if (contentEle) {
      contentEle.scrollIntoView({
        behavior: 'smooth',
        block: 'start',
      })
    }
  }, [activeLink])

  const selectedRoute = [...FlyoutOnboardingRoutes.routes].find(({ paths }) =>
    paths?.includes(activeLink)
  )

  const callBackFlyoutProduct = (productId: string) => {
    if (productId) {
      const params = new URLSearchParams(location.search)
      params.delete('cloneProductId')
      params.delete('isDraftListing')

      const pathname =
        cloud && routeAccountId
          ? `/flyout/${cloud.toLowerCase()}/${routeAccountId}/products-listing/${productId}/edit`
          : `/flyout/products-listing/${productId}/edit`

      navigate(
        {
          pathname,
          search: params.toString(),
        },
        { replace: true }
      )
    } else {
      setLockStatus('')
    }
  }
  useEffect(() => {
    return () => {
      if (currentAccountId) {
        dispatch(getProducts(currentAccountId, CloudType.AWS))
        dispatch(setErrorOnFinalSubmit(''))
      }
    }
  }, [currentAccountId])

  useEffect(() => {
    const page = getPageProductCreation(
      selectedRoute?.paths ? selectedRoute?.paths[0] : '',
      isMigrated
    )
    page && setPage(page)
  }, [selectedRoute?.paths?.[0], isMigrated])

  useEffect(() => {
    if (!flagLoadingFreeTrial) {
      if (currentAccountId) {
        if (cloneProductId) {
          dispatch(
            getProductCloneDetail({
              cloudType: CloudType.AWS,
              partnerId: currentAccountId,
              awsProductType: awsProductType as FlyOutAWSProductListingType,
              listingType: listingType as FlyOutListingUserType,
              productId: cloneProductId,
              callBackFlyoutProduct,
              isDraftListing: isDraftListing === 'true' ? true : false,
              isFreeTrialProductEnable: isFreeTrialProductEnable?.value,
            })
          )
        } else {
          dispatch(
            getProductInCreation({
              cloudType: CloudType.AWS,
              partnerId: currentAccountId,
              awsProductType: awsProductType as FlyOutAWSProductListingType,
              listingType: listingType as FlyoutListingProductType,
              productId,
              isFreeTrialProductEnable: isFreeTrialProductEnable?.value,
            })
          )
        }
      }
    }
  }, [
    currentAccountId,
    awsProductType,
    productId,
    cloneProductId,
    isFreeTrialProductEnable,
    flagLoadingFreeTrial,
  ])

  useEffect(() => {
    if (flyoutProductForm) {
      const lockMetadata =
        flyoutProductForm[FlyoutProductAddEditSectionName.LOCK_METADATA]
      if (lockMetadata) {
        if (
          lockMetadata.lockedBy &&
          lockMetadata.lockedBy === userProfile.id &&
          lockMetadata.windowId === window.name
        ) {
          setLockStatus('')
        }
      }
    }
  }, [flyoutProductForm[FlyoutProductAddEditSectionName.LOCK_METADATA]])

  useEffect(() => {
    if (lockStatus === '') {
      setStartEditTime(DateTime.now().toUTC().toISO())
    } else {
      setStartEditTime(undefined)
    }
  }, [lockStatus])

  useEffect(() => {
    if (!isEmpty(flyoutProductForm)) {
      const lockMetadata =
        flyoutProductForm[FlyoutProductAddEditSectionName.LOCK_METADATA]
      if (lockMetadata) {
        const timeDiffnew =
          Math.ceil(
            DateTime.now().diff(
              startEditTime ? DateTime.fromISO(startEditTime) : DateTime.now(),
              ['seconds']
            ).seconds
          ) > autoSaveThresoldTime
        if (lockMetadata.lockedBy && lockMetadata.lockedBy !== userProfile.id) {
          if (lockStatus === '' && timeDiffnew) {
            showModalLock()
          }
          setLockStatus(footerStateType.LOCKED)
        } else {
          if (lockMetadata.windowId !== window.name) {
            if (lockStatus === '' && timeDiffnew) {
              showModalLock()
            }
            setLockStatus(footerStateType.LOCKED)
          }
        }
      }
    }
  }, [
    flyoutProductForm[FlyoutProductAddEditSectionName.LOCK_METADATA]
      ?.lastActivityAt,
    flyoutProductForm[FlyoutProductAddEditSectionName.LOCK_METADATA],
  ])

  const disableTooltipMsg = getDisableFormLockTooltip(
    isFormLocked,
    flyoutProductForm[FlyoutProductAddEditSectionName.LOCK_METADATA]
      ?.lockedByName
  )

  const windowId = uuidv4()
  const lastActivityAt = DateTime.now().toUTC().toISO()
  if (!window.name) {
    window.name = windowId
  }

  const latestLockMetadata: LockMetadata = {
    windowId: window.name,
    lockedBy: userProfile?.id as string,
    lastActivityAt: lastActivityAt,
    lockedByName:
      `${userProfile.firstName || ''} ${userProfile.lastName || ''}`.trim(),
    lockByEmail: userProfile.email,
  }

  const completePercentage = useMemo(
    () =>
      getProductCompletePercentage(
        flyoutProductForm[FlyoutProductAddEditSectionName.PAGE_MARKER]
      ),
    [flyoutProductForm[FlyoutProductAddEditSectionName.PAGE_MARKER]]
  )

  const awsRequirementElement = document.getElementById('aws-requirements')
  const teamMemberElement = document.getElementById('team-members')
  const videoSectionElement = document.getElementById('video-section')

  useEffect(() => {
    if (
      !isEmpty(flyoutProductForm) &&
      awsRequirementElement &&
      teamMemberElement &&
      videoSectionElement &&
      userProfile &&
      isTargetReady &&
      !walkThrough.run &&
      walkThrough?.stepIndex === 0
    ) {
      const isNewUser =
        !userProfile?.walkthroughs ||
        !userProfile?.walkthroughs?.flyout ||
        !userProfile?.walkthroughs?.flyout?.productListingWalkthrough

      const checkRefs = () => {
        if (
          awsRequirementElement &&
          teamMemberElement &&
          videoSectionElement &&
          isNewUser
        ) {
          setWalkThrough(prev => ({ ...prev, run: true, steps }))
        }
      }
      checkRefs()
    }
  }, [
    flyoutProductForm,
    awsRequirementElement,
    teamMemberElement,
    videoSectionElement,
    userProfile,
    isTargetReady,
    walkThrough,
  ])

  if (isEmpty(flyoutProductForm)) {
    return <></>
  }

  return (
    <>
      <div data-testid={'add-edit-product-listing'} className={classes.root}>
        {!isImpartner && (
          <div className={classes.headerContainer}>
            <div className={classes.appTitleHeading}>
              <div
                className={classes.appBackIcon}
                data-testid="back-button"
                onClick={() => navigate(-1)}
              >
                <FontAwesomeIcon
                  icon={faArrowLeft}
                  style={{
                    width: '24px',
                    height: '24px',
                    fontSize: '24px',
                    padding: '0px 12px 0px 0px',
                    color: '#1942C5',
                  }}
                />
              </div>
              <Typography
                variant="h1"
                data-testid="heading"
                className={classes.appTitleHeadingText}
              >
                {' '}
                <ShortenedNameV2
                  name={
                    cloneProductName
                      ? `(Clone) ${cloneProductName}`
                      : 'Create new listing'
                  }
                  length={15}
                ></ShortenedNameV2>
              </Typography>
              <TitleDraftBadge />
            </div>

            <SelectedCloudIcon />
          </div>
        )}
        {listingType === FlyoutListingProductType.OTHER && (
            <div className={classes.SFListingBanner}>
              <span className={classes.SFListingBannerText}>{SFBanerText}</span>
            </div>
          )}
        <div className={classes.contentContainer}>
          {!hideMultipleSections && (
            <SidePanelV3
              activeLink={activeLink}
              onlyActions
              title="Listing creation steps -"
              showProgress
              progressCount={completePercentage}
              routes={FlyoutOnboardingRoutes.routes}
              openDrawer={true}
              navigateRoute={navigateRoute}
              footerSubMenuRoutes={[]}
              middleMicellaneousSection={
                isImpartner ? <></> : <MiddleSection page={page} />
              }
              userInfo={{
                name: 'string',
                roles: [],
              }}
              isNew={false}
              customFooter={isImpartner ? <></> : <TeamMembersV2 />}
            />
          )}
          <div
            className={
              !hideMultipleSections
                ? classes.contentWrapper
                : classes.contentWrapperFullWidth
            }
          >
            {isSFFirstTime &&
            isEmpty(flyoutProductForm) &&
            listingType === FlyoutListingProductType.OTHER ? (
              <SalesforceLoadingScreen />
            ) : (
              <>
                {!isEmpty(flyoutProductForm) ? (
                  <div className={classes.content}>
                    <div id="product-creation-top">
                      {!isEmpty(isErrorOnFinalSubmit) ? (
                        <div
                          className={classes.statusBar}
                          data-testid="status-bar-component"
                        >
                          <StatusBar
                            status={'Failed'}
                            content={`${companyName} needs to resubmit their draft listing form so that listing can be created.`}
                            backgroundColor={StatusBarColors.RED}
                          />
                        </div>
                      ) : (
                        <></>
                      )}

                      {!isEmpty(isFailedError) ? (
                        <div
                          className={classes.statusBar}
                          data-testid="status-bar-component"
                        >
                          <StatusBar
                            status={'Failed'}
                            content={
                              <div>
                                {companyName} needs to resubmit their draft
                                listing form so that listing can be created.
                              </div>
                            }
                            backgroundColor={StatusBarColors.RED}
                            dividerColor={StatusBarDividerColors.SECONDARY}
                            statusBarChipType="secondary"
                            subContent={
                              <div
                                className={classes.errorSubContent}
                                data-testid={'failed-offer-content'}
                              >
                                <div className={classes.errorMsg}>
                                  Issues found: {isFailedError}
                                </div>
                                <div className={classes.offerUpdatedAt}>
                                  {updatedAtValue}
                                </div>
                              </div>
                            }
                          />
                        </div>
                      ) : (
                        <></>
                      )}
                      {/* <div className={classes.title} data-testid={'title'}>
                      </div> */}
                    </div>
                    <Formik
                      initialValues={flyoutProductForm}
                      validationSchema={FlyoutProductValidationSchema}
                      validateOnMount={true}
                      onSubmit={() => {}}
                      enableReinitialize
                    >
                      {formikProps => {
                        const handleSubmitProductForm = useCallback(
                          async (handleSubmit: (status?: string) => void) => {
                            await formikProps.submitForm()
                            const isCurrentPageHaveErr =
                              await validateProductCreationFlow(
                                page,
                                formikProps,
                                false,
                                false,
                                {
                                  isFreeTrialEnable:
                                    isFreeTrialProductEnable?.value,
                                }
                              )
                            if (!isCurrentPageHaveErr) {
                              handleSubmit()
                            }
                          },
                          [formikProps, page]
                        )

                        return (
                          <Form>
                            <ScrollToFieldError />
                            <div className={classes.contentBody}>
                              <LabraToolTipV2
                                tooltipPlacement="bottom-start"
                                description={
                                  lockStatus === footerStateType.LOCKED
                                    ? disableTooltipMsg
                                    : lockStatus === footerStateType.UNLOCKED
                                      ? 'Click on “Start editing” below to edit the form.'
                                      : ''
                                }
                                tooltipPlacmentGap="32px"
                                followCursor={isFormLocked}
                              >
                                <div
                                  className={clsx(
                                    classes.formContainer,
                                    isFormLocked ? classes.formDisabled : ''
                                  )}
                                >
                                  <div
                                    className={
                                      isFormLocked ? classes.disableEvents : ''
                                    }
                                  >
                                    {(page ===
                                      FlyoutProductAddEditSectionName.GENERAL_INFORMATION ||
                                      page ===
                                        FlyoutProductAddEditSectionName.MIGRATION_DETAILS) && (
                                      <FlyOutProductDetails
                                        isFormLocked={isFormLocked}
                                        isImpartner={isImpartner}
                                      />
                                    )}
                                    {page ===
                                      FlyoutProductAddEditSectionName.NOTIFICATIONS_RECIPIENTS && (
                                      <FlyOutNotificationSection
                                        isFormLocked={isFormLocked}
                                      />
                                    )}
                                    {page ===
                                      FlyoutProductAddEditSectionName.LANDING_PAGE_DETAILS && (
                                      <FlyoutLandingPageSection
                                        isFormLocked={isFormLocked}
                                      />
                                    )}
                                    {page ===
                                      FlyoutProductAddEditSectionName.PRODUCT_DIMENSIONS &&
                                      !isMigrated && (
                                        <FlyoutProductDimensionsSection
                                          isFormLocked={isFormLocked}
                                        />
                                      )}
                                  </div>
                                </div>
                              </LabraToolTipV2>
                            </div>
                            <AddEditProductListingFooter
                              activeLink={activeLink}
                              setActiveLink={setActiveLink}
                              page={page}
                              lockStatus={lockStatus}
                              setLockStatus={setLockStatus}
                              userProfileId={userProfile?.id as string}
                              latestLockMetadata={latestLockMetadata}
                              isFormLocked={isFormLocked}
                              productId={productId}
                              disableTooltipMsg={disableTooltipMsg}
                              handleSubmitForm={handleSubmitProductForm}
                              isImpartner={isImpartner}
                              hideMultipleSections={hideMultipleSections}
                            />
                            <div>
                              <ModalConfirmV2
                                title={'Lock released'}
                                content={
                                  <Typography className={classes.modalText}>
                                    Your editing session expired due to
                                    inactivity, and the form is now being edited
                                    by{' '}
                                    <span className={classes.nameBoldText}>
                                      {flyoutProductForm?.[
                                        FlyoutProductAddEditSectionName
                                          .LOCK_METADATA
                                      ]?.lockedByName || ''}
                                    </span>
                                    . Check back later.
                                  </Typography>
                                }
                                open={isOpenLockModal}
                                titleAcceptButton={'Ok'}
                                onAccept={handleCloseLock}
                                onDecline={handleCloseLock}
                                variant={ModalSizeVariants.small}
                                acceptDisabled={false}
                                hideCancelButton={true}
                                icon={
                                  <FontAwesomeIcon
                                    icon={faOctagonExclamation}
                                    style={{ width: '20px', height: '16px' }}
                                  />
                                }
                              />
                            </div>
                          </Form>
                        )
                      }}
                    </Formik>
                  </div>
                ) : (
                  <></>
                )}
              </>
            )}
          </div>
        </div>
        <div style={{ position: 'relative' }}>
          {!isImpartner && (
            <Joyride
              callback={handleJoyrideCallback}
              continuous
              run={walkThrough.run}
              scrollOffset={64}
              scrollToFirstStep
              showProgress
              disableOverlayClose
              floaterProps={{
                hideArrow: true,
              }}
              tooltipComponent={WalkthroughTooltip}
              steps={walkThrough.steps}
              styles={{
                tooltip: {
                  zIndex: 10000,
                },
                options: {
                  zIndex: 10000,
                },
              }}
            />
          )}
        </div>
        <SnackBar />
      </div>
    </>
  )
}

const MiddleSection = ({ page }: { page: FlyoutProductAddEditSectionName }) => {
  const { classes } = useStyles()
  const videoSectionInfo = getVideoSectionInfo({ page })
  return (
    <div className={classes.middleSectionContainer}>
      <Divider />
      <RequirementsActionV2
        link={awsRequirementsLinkLabra}
        linkText={awsRequirementsText}
        id={'aws-requirements'}
      />
      {videoSectionInfo?.videoLink && (
        <ProductVideoAction
          actionTitle={videoSectionInfo?.actionTitle}
          actionLable={videoSectionInfo?.actionLable}
          videoLink={videoSectionInfo?.videoLink}
          tooltip={videoSectionInfo?.tooltip}
          id={'video-section'}
        />
      )}
    </div>
  )
}

const getVideoSectionInfo = ({
  page,
}: {
  page: FlyoutProductAddEditSectionName
}) => {
  switch (page) {
    case FlyoutProductAddEditSectionName.GENERAL_INFORMATION: {
      return {
        actionTitle: 'Product Details: ',
        actionLable: 'Tips for a Perfect Listing',
        videoLink:
          'https://www.loom.com/embed/d89eb9ecd93a47bab90086a2c06d001f?sid=12b0f639-e653-4f2a-aef9-3a2bff7f05c5',
        tooltip:
          'Create a standout listing—learn how to complete Product Details the right way!',
      }
    }
    case FlyoutProductAddEditSectionName.PRODUCT_DIMENSIONS: {
      return {
        actionTitle: 'Pricing Your Listing:',
        actionLable: 'Product Dimensions Guide',
        videoLink:
          'https://www.loom.com/embed/ac84b1d92abc483cb90eeba74f431e24?sid=330cc95a-f5c1-4e48-bcf2-e2e28b6c20e1',
        tooltip:
          'Fine-tune your pricing—learn how to set up product dimensions effectively!',
      }
    }
    case FlyoutProductAddEditSectionName.LANDING_PAGE_DETAILS: {
      return {
        actionTitle: 'Buyer Onboarding:',
        actionLable: 'Registration & Access Guide',
        videoLink:
          'https://www.loom.com/embed/866b4c57346e4b848861da1958cc1df6?sid=f0ce5cf9-f2ad-4cc1-9c1c-68dcaa651199',
        tooltip:
          'Ensure a seamless buyer journey—learn how to set up and optimize your buyer registration page!',
      }
    }
    case FlyoutProductAddEditSectionName.NOTIFICATIONS_RECIPIENTS: {
      return {
        actionTitle: 'Managing Notifications:',
        actionLable: 'Configure & Customize alerts',
        videoLink:
          'https://www.loom.com/embed/5688baf84f274c7db72f807bf156a6bd?sid=e51720da-d2ab-499e-86ae-84322b02b03c',
        tooltip:
          'Stay informed—learn how to control and customize your marketplace notifications!',
      }
    }
    case FlyoutProductAddEditSectionName.MIGRATION_DETAILS: {
      return {
        actionTitle: 'Product Listings:',
        actionLable: `Migration Do's & Don'ts`,
        videoLink: '',
        tooltip:
          'Migrate like a pro -discover the smart way to move listings to Labra.',
      }
    }
    default: {
      return {
        actionTitle: '',
        actionLable: '',
        videoLink: '',
        tooltip: '',
      }
    }
  }
}
